import { atom } from "jotai";
import { atomEffect } from "jotai-effect";
import { loadable } from "jotai/utils";

import { Logger } from "@moment/logging";

import { $loggedInUser } from "../authenication/token.atom";
import { $commandAvailability } from "../terminal/command-availability.atom";
import { $anonymousUserId } from "./anonymous-id.atom";

const logger = new Logger("product-analytics");

// ── CLI tools detection ─────────────────────────────────────────────────

let hasFiredCliEvent = false;

export const $cliToolsAnalyticsEffect = atomEffect((get) => {
	const cmdLoadable = get(loadable($commandAvailability));
	const anonIdLoadable = get(loadable($anonymousUserId));

	if (cmdLoadable.state !== "hasData" || anonIdLoadable.state !== "hasData" || hasFiredCliEvent) {
		return;
	}

	hasFiredCliEvent = true;

	const user = get($loggedInUser);
	const cmds = cmdLoadable.data;
	const userId = user?.sub ?? anonIdLoadable.data;

	logger.info("product.cli_tools_detected", {
		userId,
		isAnonymous: !user?.sub,
		claude: cmds.claude,
		amp: cmds.amp,
		codex: cmds.codex,
		copilot: cmds.copilot,
	});
});
$cliToolsAnalyticsEffect.debugLabel = "cliToolsAnalyticsEffect";

// ── Terminal discovery ──────────────────────────────────────────────────

let hasFiredTerminalEvent = false;

/**
 * Write-only atom to track terminal discovery. Call this when the terminal
 * is first made visible. Fires at most once per app session.
 */
export const $trackTerminalDiscovered = atom(null, async (get) => {
	if (hasFiredTerminalEvent) {
		return;
	}
	hasFiredTerminalEvent = true;

	const user = get($loggedInUser);
	const anonId = await get($anonymousUserId);
	const userId = user?.sub ?? anonId;
	const cmds = get(loadable($commandAvailability));

	logger.info("product.terminal_discovered", {
		userId,
		isAnonymous: !user?.sub,
		...(cmds.state === "hasData" && {
			claude: cmds.data.claude,
			amp: cmds.data.amp,
			codex: cmds.data.codex,
			copilot: cmds.data.copilot,
		}),
	});
});
$trackTerminalDiscovered.debugLabel = "trackTerminalDiscovered";

// ── Document open ───────────────────────────────────────────────────────

export type DocumentOpenMeta = {
	documentId: string;
	workspaceName: string;
};

/**
 * Write-only atom to track when a document is opened. Fires once per
 * navigation to a different document (not on reload/purge of the same doc).
 */
export const $trackDocumentOpened = atom(null, async (get, _set, event: DocumentOpenMeta) => {
	const user = get($loggedInUser);
	const anonId = await get($anonymousUserId);
	const userId = user?.sub ?? anonId;

	logger.info("product.document_opened", {
		userId,
		isAnonymous: !user?.sub,
		...event,
	});
});
$trackDocumentOpened.debugLabel = "trackDocumentOpened";

// ── Document creation ───────────────────────────────────────────────────

export type DocumentCreationSource =
	| "templateGallery"
	| "dropdown"
	| "onboarding"
	| "onboardingPreview";

export type DocumentCreationMeta = {
	type: "blank" | "template";
	templateId?: string;
	templateTitle?: string;
	source: DocumentCreationSource;
};

/**
 * Write-only atom to track successful document creation. Logs whether the
 * user chose a blank document or a specific template, and where the action
 * originated.
 */
export const $trackDocumentCreated = atom(null, async (get, _set, event: DocumentCreationMeta) => {
	const user = get($loggedInUser);
	const anonId = await get($anonymousUserId);
	const userId = user?.sub ?? anonId;

	logger.info("product.document_created", {
		userId,
		isAnonymous: !user?.sub,
		...event,
	});
});
$trackDocumentCreated.debugLabel = "trackDocumentCreated";

/**
 * Write-only atom to track failed document creation attempts.
 */
export const $trackDocumentCreationFailed = atom(
	null,
	async (get, _set, event: DocumentCreationMeta & { error: string }) => {
		const user = get($loggedInUser);
		const anonId = await get($anonymousUserId);
		const userId = user?.sub ?? anonId;

		logger.info("product.document_creation_failed", {
			userId,
			isAnonymous: !user?.sub,
			...event,
		});
	}
);
$trackDocumentCreationFailed.debugLabel = "trackDocumentCreationFailed";

// ── Document share ──────────────────────────────────────────────────────

export type DocumentShareMeta = {
	documentId: string;
	workspaceSlug: string;
	isFirstShare: boolean;
};

/**
 * Write-only atom to track when a document is shared. Includes an
 * `isFirstShare` attribute so callers can distinguish first-time shares.
 */
export const $trackDocumentShared = atom(null, async (get, _set, event: DocumentShareMeta) => {
	const user = get($loggedInUser);
	const anonId = await get($anonymousUserId);
	const userId = user?.sub ?? anonId;

	logger.info("product.document_shared", {
		userId,
		isAnonymous: !user?.sub,
		...event,
	});
});
$trackDocumentShared.debugLabel = "trackDocumentShared";

// ── Document publish ────────────────────────────────────────────────────

export type DocumentPublishMeta = {
	documentId: string;
	workspaceSlug: string;
	isFirstPublish: boolean;
};

/**
 * Write-only atom to track when a document is published. Includes an
 * `isFirstPublish` attribute so callers can distinguish first-time publishes.
 */
export const $trackDocumentPublished = atom(null, async (get, _set, event: DocumentPublishMeta) => {
	const user = get($loggedInUser);
	const anonId = await get($anonymousUserId);
	const userId = user?.sub ?? anonId;

	logger.info("product.document_published", {
		userId,
		isAnonymous: !user?.sub,
		...event,
	});
});
$trackDocumentPublished.debugLabel = "trackDocumentPublished";

// ── Test helpers ────────────────────────────────────────────────────────

export const resetProductAnalytics = () => {
	hasFiredCliEvent = false;
	hasFiredTerminalEvent = false;
};
